import {DrupalNode} from "next-drupal";
import {HTMLProps} from "react";
import {Button} from "../../atoms/button";
import classNames from "classnames";
import {hasCmsIcon, ImageIcon} from "../../atoms/icon";
import dynamic from "next/dynamic";

const FormattedText = dynamic(() => import("../../formatted-text").then((mod) => mod.FormattedText));

interface BenefitSectionProps extends HTMLProps<HTMLDivElement> {
  node: DrupalNode,
}

function BenefitCard({node}: {node: DrupalNode}) {
  const defaultBenefit = node.field_benefit_default_benefit;
  const title = node?.field_benefit_title_override ?? defaultBenefit.field_benefit_display_title;
  const smallDescription = node?.field_benefit_desc_sm_override ?? defaultBenefit.field_benefit_small_description;
  const largeDescription = node?.field_benefit_desc_lg_override ?? defaultBenefit.field_benefit_description_large;
  const link = node?.field_benefit_link;
  const buttonType = node?.field_benefit_button_type ?? "regular";
  const icon = node?.field_benefit_default_benefit?.field_benefit_icon;
  const iconPosition = node?.field_benefit_icon_pos_override
    ?? node?.field_benefit_default_benefit?.field_benefit_icon_position
    ?? "left";

  return (
    <div key={node.id} className="bg-white p-12 text-center drop-shadow-sm">
      {(hasCmsIcon(icon) && iconPosition === "top") && (
        <div className="mb-2 h-12">
          <ImageIcon icon={icon} color="primary" width={50} height={50} alt={title} className={"m-auto object-contain max-h-full"}/>
        </div>
      )}
      <div className="font-bold text-primary text-2xl pb-3">
        <div className="flex items-center justify-center">
          {(hasCmsIcon(icon) && iconPosition === "left") && (
            <ImageIcon icon={icon} color="primary" width={25} height={25} alt={title} className="mr-3" />
          )}
          {title}
        </div>
      </div>
      {largeDescription && (
        <div className="font-light text-2xl pb-3">{largeDescription}</div>
      )}
      {smallDescription && (
        <div className="font-light pb-3">{smallDescription}</div>
      )}
      {(link && buttonType === "regular") && (
        <Button href={link.uri} target={"_blank"} className="text-sm mt-3" invert={true} hover={{color: "primary"}}>{link.title}</Button>
      )}
      {(link && buttonType === "arrow") && (
        <Button href={link.uri} target={"_blank"} className="text-sm border-0 hover:border-0"
                icon={"arrow-up-right"} position={"right"}
                invert={true}>{link.title}</Button>
      )}
    </div>
  )
}

/**
 * Renders a benefit section node.
 *
 * The following fields should be included on the node to render correctly.:
 * - field_benefits_benefits
 * - field_benefits_benefits.field_benefit_default_benefit
 *
 * @param node
 * @constructor
 */
export function NodeBenefitSection({node, ...props}: BenefitSectionProps) {

  if (!node?.field_benefits_benefits?.length) {
    return <></>
  }

  node.field_benefits_benefits = node.field_benefits_benefits.filter(item => item?.type !== "unknown" && item?.id !== "missing");

  return (
    <div className={classNames("container mx-auto", props?.className ?? "")}>
      <h3 className="md:!text-4xl pb-6">{node.title}</h3>
      {node?.body?.processed && (
        <div className="text-xl font-light">
          <FormattedText processed={node.body.processed}/>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 pt-8">
        {node.field_benefits_benefits.map(item => <BenefitCard node={item} key={item.id}/>)}
      </div>
    </div>
  )
}